(() => {
  const elementName = 'kago-instagram-feed';

  if (customElements.get(elementName)) {
    return;
  }

  class KagoInstagramFeed extends HTMLElement {
    connectedCallback() {
      this.source = this.querySelector('[data-kago-instagram-source]');
      this.liveGrid = this.querySelector('[data-kago-instagram-live]');
      this.overlayTemplate = this.querySelector('[data-kago-instagram-overlay-template]');

      if (!this.source || !this.liveGrid) {
        return;
      }

      this.handleFeedLoad = () => this.scheduleSync();
      document.addEventListener('instafeedAppLoaded', this.handleFeedLoad);

      this.observer = new MutationObserver(() => this.scheduleSync());
      this.observer.observe(this.source, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset', 'data-src', 'data-srcset', 'data-lazy-src', 'data-original', 'poster', 'href']
      });

      this.retryTimers = [500, 1500, 3500].map((delay) => window.setTimeout(() => this.scheduleSync(), delay));
      this.scheduleSync();
    }

    disconnectedCallback() {
      document.removeEventListener('instafeedAppLoaded', this.handleFeedLoad);
      this.observer?.disconnect();
      this.retryTimers?.forEach((timer) => window.clearTimeout(timer));

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
      }
    }

    scheduleSync() {
      if (this.frame) {
        return;
      }

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    sync() {
      this.source.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      const posts = this.collectPosts();

      if (posts.length === 0) {
        return;
      }

      const signature = posts.map((post) => `${post.imageUrl}|${post.href}`).join('::');

      if (signature === this.signature) {
        return;
      }

      this.signature = signature;
      this.liveGrid.replaceChildren(...posts.map((post) => this.createPost(post)));
      this.liveGrid.hidden = false;
      this.classList.add('is-ready');
    }

    collectPosts() {
      const limit = Math.max(1, Number.parseInt(this.dataset.postLimit, 10) || 5);
      const posts = [];
      const seen = new Set();
      const ignoredAncestors = '[class*="avatar" i], [class*="profile" i], [class*="logo" i], [class*="spinner" i], [class*="loading" i]';
      const accountHandle = this.getAccountHandle();
      const layoutItems = this.source.querySelectorAll('[data-instafeed-thumbnail]');

      for (const item of layoutItems) {
        const imageUrl = item.getAttribute('data-instafeed-thumbnail') || '';
        const href = this.getPostUrl(item);

        if (!imageUrl || /(?:placeholder|spinner|loading|avatar|profile|logo|icon)/i.test(imageUrl) || seen.has(imageUrl)) {
          continue;
        }

        seen.add(imageUrl);
        posts.push({
          imageUrl,
          srcset: '',
          href,
          alt: accountHandle ? `Instagram post by @${accountHandle}` : 'KAGO Instagram post'
        });

        if (posts.length >= limit) {
          return posts;
        }
      }

      for (const media of this.source.querySelectorAll('img, video')) {
        if (media.closest(ignoredAncestors)) {
          continue;
        }

        const alt = media.getAttribute('alt') || '';
        const imageUrl = this.getImageUrl(media);

        if (!imageUrl || /(?:placeholder|spinner|loading|avatar|profile|logo|icon)/i.test(imageUrl) || /(?:profile picture|avatar|logo)/i.test(alt)) {
          continue;
        }

        const href = this.getPostUrl(media);
        const key = `${imageUrl}|${href}`;

        if (seen.has(key)) {
          continue;
        }

        seen.add(key);
        posts.push({
          imageUrl,
          srcset: media.tagName === 'IMG' ? (media.getAttribute('data-srcset') || media.getAttribute('srcset') || '') : '',
          href,
          alt: alt || (accountHandle ? `Instagram post by @${accountHandle}` : 'KAGO Instagram post')
        });

        if (posts.length >= limit) {
          break;
        }
      }

      return posts.slice(0, limit);
    }

    getImageUrl(media) {
      const layoutItem = media.closest('[data-instafeed-thumbnail]');
      const candidates = media.tagName === 'VIDEO'
        ? [layoutItem?.getAttribute('data-instafeed-thumbnail'), media.getAttribute('data-poster'), media.getAttribute('poster')]
        : [
            layoutItem?.getAttribute('data-instafeed-thumbnail'),
            media.getAttribute('data-instafeed-src'),
            media.getAttribute('data-src'),
            media.getAttribute('data-lazy-src'),
            media.getAttribute('data-original'),
            media.currentSrc,
            media.getAttribute('src')
          ];

      return candidates.find((value) => value && !value.startsWith('data:') && !value.startsWith('blob:')) || '';
    }

    getPostUrl(media) {
      const accountUrl = this.dataset.accountUrl || '';
      const directLink = media.closest('a[href]');
      const containerLink = media.closest('[class*="instafeed" i], [class*="feed" i]')?.querySelector('a[href*="instagram.com"]');
      const href = directLink?.href || containerLink?.href || '';

      if (/^https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\//i.test(href)) {
        return href;
      }

      return accountUrl;
    }

    getAccountHandle() {
      try {
        return new URL(this.dataset.accountUrl).pathname.split('/').filter(Boolean)[0] || '';
      } catch (error) {
        return '';
      }
    }

    createPost(post) {
      const element = document.createElement(post.href ? 'a' : 'div');
      element.className = 'kago-instagram__post';
      element.setAttribute('role', 'listitem');

      if (post.href) {
        element.href = post.href;
        element.setAttribute('aria-label', post.alt);

        if (this.hasAttribute('data-open-in-new-tab')) {
          element.target = '_blank';
          element.rel = 'noopener noreferrer';
        }
      }

      const image = document.createElement('img');
      image.src = post.imageUrl;
      image.alt = post.alt;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.sizes = '(max-width: 699px) 78vw, (max-width: 999px) 33vw, 20vw';

      if (post.srcset) {
        image.srcset = post.srcset;
      }

      element.append(image);

      if (post.href && this.overlayTemplate) {
        element.append(this.overlayTemplate.content.cloneNode(true));
      }

      return element;
    }
  }

  customElements.define(elementName, KagoInstagramFeed);
})();
